import { useEffect, useState } from "react";
import { PlaylistSetting } from "@/components/PlaylistSetting";
import { Check, Download } from "lucide-react";
import { API, http, login } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const addr = (s) => (s ? [s.name, s.line1, s.line2, s.city, s.state, s.postal_code, s.country].filter(Boolean).join(", ") : "—");

const DesignCard = ({ d, onToggle }) => (
  <article className="rounded-[22px] border border-purple-300/20 bg-[#170a24] overflow-hidden" data-testid={`owner-design-${d.id}`}>
    <img src={`${API}/designs/${d.id}/image.svg`} alt="Customer bracelet design" className="w-full aspect-square object-cover" />
    <div className="p-5 space-y-2 text-sm">
      {d.bundle && (
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-200" data-testid={`owner-design-bundle-${d.id}`}>
          {d.bundle} pair set · {d.label}
        </p>
      )}
      <p className="font-display text-2xl">{d.customer_name}</p>
      <p className="text-purple-200">{d.email}</p>
      <p className="text-purple-100"><span className="text-amber-200">Ship to:</span> {addr(d.shipping)}</p>
      {d.note && <p className="text-purple-100"><span className="text-amber-200">Note:</span> {d.note}</p>}
      {d.gift_note && (
        <p className="text-purple-100 rounded-xl border border-pink-200/30 px-3 py-2" data-testid={`owner-design-gift-${d.id}`}>
          <span className="text-pink-200">Gift note:</span> {d.gift_note}
        </p>
      )}
      {d.charm_text && (
        <p className="text-purple-100 rounded-xl border border-amber-200/30 px-3 py-2" data-testid={`owner-design-charm-${d.id}`}>
          <span className="text-amber-200">Write on package ({d.charm_title}):</span> {d.charm_text}
        </p>
      )}
      <p className="text-purple-200">{new Date(d.paid_at || d.created_at).toLocaleString()}</p>
      <div className="flex gap-2 pt-2">
        <button onClick={() => onToggle(d.id)} data-testid={`owner-design-made-${d.id}`}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 border transition-colors ${d.made ? "bg-amber-200 text-[#0e0616] border-amber-200" : "border-purple-200/40 hover:border-amber-200"}`}>
          <Check className="w-4 h-4" /> {d.made ? "Made" : "Mark as made"}
        </button>
        <a href={`${API}/designs/${d.id}/image.png`} download={`design-${d.id}.png`} target="_blank" rel="noreferrer"
          data-testid={`owner-design-download-${d.id}`} className="inline-flex items-center gap-2 rounded-full px-4 py-2 border border-purple-200/40 hover:border-amber-200">
          <Download className="w-4 h-4" /> Picture
        </a>
      </div>
    </div>
  </article>
);

export default function OwnerPage() {
  const { user, loading } = useAuth();
  const [designs, setDesigns] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!user) return;
    http.get("/owner/designs")
      .then((r) => setDesigns(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDenied(true));
  }, [user]);

  const toggle = async (id) => {
    try {
      const { data } = await http.patch(`/owner/designs/${id}`);
      setDesigns((p) => p.map((d) => (d.id === id ? { ...d, made: !!data?.made } : d)));
    } catch { /* ignore */ }
  };

  const msg = (text, action) => (
    <main className="min-h-screen grid place-items-center px-6 pt-20 text-center" data-testid="owner-page-message">
      <div><p className="font-display text-4xl">{text}</p>{action}</div>
    </main>
  );
  if (loading) return msg("…");
  if (!user) return msg("Owner studio", <button onClick={login} data-testid="owner-login-btn" className="mt-6 rounded-full bg-purple-200 text-[#0e0616] px-7 py-3">Sign in</button>);
  if (denied) return msg("This page is only for Liza.");

  return (
    <main className="min-h-screen pt-32 pb-24" data-testid="owner-page">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-purple-200">Owner studio</p>
        <h1 className="font-display text-5xl md:text-6xl mt-4">Custom designs to make</h1>
        <p className="mt-4 text-purple-100/85">Paid custom bracelet orders, newest first.</p>
        <PlaylistSetting />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {(designs ?? []).map((d) => <DesignCard key={d.id} d={d} onToggle={toggle} />)}
        </div>
        {designs?.length === 0 && <p className="font-display italic text-2xl text-purple-100 py-16" data-testid="owner-empty">No paid designs yet.</p>}
      </div>
    </main>
  );
}
